import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
  Card,
  Tag,
  Tooltip,
  Row,
  Col,
  Typography,
  Empty,
  Divider,
  Statistic,
  Progress,
  Badge,
  Alert,
  Spin,
  Radio,
  Dropdown,
  Menu,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  LinkOutlined,
  SettingOutlined,
  BarChartOutlined,
  UserOutlined,
  GlobalOutlined,
  MobileOutlined,
  DesktopOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  UploadOutlined,
  CopyOutlined,
  ShareAltOutlined,
  LockOutlined,
  UnlockOutlined,
  ThunderboltOutlined,
  LineChartOutlined,
  FileTextOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { linkService, Link } from '../services/link';
import { ruleService } from '../services/rule';
import { domainService, Domain } from '../services/domain';
import { useNavigate } from 'react-router-dom';
import RuleConfigModal, { RuleConfigMode, RuleConfigItem } from '../components/RuleConfigModal';
import { LINK_STATUS_CONFIG, RULE_TYPES, getStatusConfig, getRuleTypeInfo } from '../constants';
import { useDeviceConstantsStore } from '../hooks/useDeviceConstantsStore';
import { handleApiError } from '../utils/errorHandler';
import { useAuthStore } from '../stores/authStore';

const SCHEME_URL_REGEX = /^[a-zA-Z][a-zA-Z0-9+.-]*:.+/;

const { Text, Title } = Typography;
const { Option } = Select;

const splitDefaultURLString = (raw?: string) => {
  if (!raw) {
    return [] as string[];
  }
  return raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const formatDefaultURLInputValue = (raw?: string) => {
  return splitDefaultURLString(raw).join('\n');
};

const parseDefaultURLInputValue = (value?: string) => {
  if (!value) {
    return [] as string[];
  }
  return value
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const summarizeDefaultURLs = (raw?: string) => {
  const urls = splitDefaultURLString(raw);
  if (urls.length === 0) {
    return { first: '', others: 0, tooltip: '' };
  }
  return {
    first: urls[0],
    others: Math.max(urls.length - 1, 0),
    tooltip: urls.join('\n'),
  };
};

const LinkManagement: React.FC = () => {
  const navigate = useNavigate();
  const [links, setLinks] = useState<Link[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [rulesModalVisible, setRulesModalVisible] = useState(false);
  const [editingLink, setEditingLink] = useState<Link | null>(null);
  const [currentLinkRules, setCurrentLinkRules] = useState<RuleConfigItem[]>([]);
  const [ruleConfigMode, setRuleConfigMode] = useState<RuleConfigMode>('view');
  const [form] = Form.useForm();
  const deployTypeWatch = Form.useWatch('deploy_type', form);
  const jumpModeWatchRaw = Form.useWatch('jump_mode', form);
  const jumpBlockModeWatchRaw = Form.useWatch('jump_block_mode', form);

  const jumpModeWatch = jumpModeWatchRaw === undefined || jumpModeWatchRaw === null ? undefined : String(jumpModeWatchRaw);
  const jumpBlockModeWatch = jumpBlockModeWatchRaw === undefined || jumpBlockModeWatchRaw === null ? undefined : String(jumpBlockModeWatchRaw);
  const [searchText, setSearchText] = useState('');
  const [ruleIdFilter, setRuleIdFilter] = useState<string>(''); // 规则ID筛选
  const [clientIdFilter, setClientIdFilter] = useState<string>(''); // 客户ID筛选
  const [deployFilter, setDeployFilter] = useState<string>('all'); // 部署模式筛选

  // 临时输入状态（用于输入框，不触发自动搜索）
  const [searchInput, setSearchInput] = useState('');
  const [ruleIdInput, setRuleIdInput] = useState<string>('');
  const [clientIdInput, setClientIdInput] = useState<string>('');
  const [rulesList, setRulesList] = useState<any[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesHasMore, setRulesHasMore] = useState(false);
  const [rulesPage, setRulesPage] = useState(1);
  const [domainsList, setDomainsList] = useState<Domain[]>([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [createMode, setCreateMode] = useState<'api' | 'short_link' | null>(null); // 创建模式
  const [phpModalVisible, setPhpModalVisible] = useState(false); // PHP下载弹窗
  const [phpModalRecord, setPhpModalRecord] = useState<Link | null>(null); // 当前下载的链接
  const [phpDeployType, setPhpDeployType] = useState<string>('standalone'); // 部署模式
  const [phpDownloading, setPhpDownloading] = useState(false); // 下载中
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [hasMore, setHasMore] = useState(false);
  const pageTokensRef = useRef<number[]>([0]);

  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.user_type === 'admin';

  // 防抖和取消请求的引用
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const {
    initialize: initializeDeviceConstants,
    initialized: deviceConstantsInitialized,
    templateOptions: rawTemplateOptions,
    loading: deviceConstantsLoading,
  } = useDeviceConstantsStore((state) => ({
    initialize: state.initialize,
    initialized: state.initialized,
    templateOptions: state.templateOptions,
    loading: state.loading,
  }));

  useEffect(() => {
    if (!deviceConstantsInitialized) {
      initializeDeviceConstants();
    }
  }, [deviceConstantsInitialized, initializeDeviceConstants]);

  const templateSelectOptions = useMemo(() => {
    return rawTemplateOptions.map((tpl) => ({
      value: tpl.file_name,
      label: tpl.name,
      code: tpl.code,
    }));
  }, [rawTemplateOptions]);

  // 加载规则列表（支持分页加载更多）
  const loadRulesList = useCallback(async (page: number = 1, append: boolean = false) => {
    setRulesLoading(true);
    try {
      const response = await ruleService.getRuleSelectList({ page, limit: 50 });
      if (response) {
        const newList = response.shared || [];
        if (append) {
          setRulesList(prev => [...prev, ...newList]);
        } else {
          setRulesList(newList);
        }
        setRulesHasMore(response.has_more);
        setRulesPage(page);
      }
    } catch (error) {
      console.error('加载规则列表失败:', error);
      message.error('加载规则列表失败');
    } finally {
      setRulesLoading(false);
    }
  }, []);

  // 加载更多规则
  const loadMoreRules = useCallback(() => {
    if (!rulesLoading && rulesHasMore) {
      loadRulesList(rulesPage + 1, true);
    }
  }, [rulesLoading, rulesHasMore, rulesPage, loadRulesList]);

  useEffect(() => {
    const updates: Record<string, string> = {};
    const jumpModeStr = jumpModeWatch ?? '';

    if (deployTypeWatch === 'short_link') {
      if (jumpModeStr !== '1') {
        updates.jump_mode = '1';
      }
    }

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
    }
  }, [deployTypeWatch, jumpModeWatch, form]);

  // 加载域名列表
  const loadDomainsList = useCallback(async () => {
    setDomainsLoading(true);
    try {
      const response = await domainService.getDomainsList({ status: 'active', page: 1, size: 100 });
      if (response.success && response.data?.list) {
        setDomainsList(response.data.list);
      }
    } catch (error) {
      console.error('加载域名列表失败:', error);
      message.error('加载域名列表失败');
    } finally {
      setDomainsLoading(false);
    }
  }, []);

  // 加载链接数据（支持搜索 + ID游标分页）
  const loadLinks = useCallback(
    async (
      targetPage: number = 1,
      size: number = pageSize,
      overrideLastId?: number,
      keywordOverride?: string,
    ) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      const debounceDelay = keywordOverride !== undefined && keywordOverride !== searchText ? 500 : 0;
      const keyword = keywordOverride !== undefined ? keywordOverride : searchText;

      debounceTimerRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const tokens = pageTokensRef.current;
          const token = overrideLastId !== undefined
            ? overrideLastId
            : targetPage > 1
              ? tokens[targetPage - 1] ?? 0
              : 0;

          const params: any = {
            page: targetPage,
            size,
            last_id: token,
          };
          if (keyword) {
            params.keyword = keyword;
          }
          if (ruleIdFilter) {
            const ruleId = parseInt(ruleIdFilter);
            if (!isNaN(ruleId) && ruleId > 0) {
              params.rule_id = ruleId;
            }
          }
          if (clientIdFilter) {
            const clientId = parseInt(clientIdFilter);
            if (!isNaN(clientId) && clientId > 0) {
              params.client_id = clientId;
            }
          }

          const response = await linkService.getLinks(params);
          if (!abortControllerRef.current?.signal.aborted) {
            const list = response.list || [];
            let filteredList = list;
            if (deployFilter !== 'all') {
              filteredList = list.filter((link: Link) =>
                deployFilter === 'api'
                  ? !link.deploy_type || link.deploy_type === 'api'
                  : link.deploy_type === deployFilter
              );
            }

            setLinks(filteredList);
            setTotal((prevTotal) =>
              response.total !== undefined
                ? response.total
                : targetPage === 1
                  ? filteredList.length
                  : prevTotal
            );
            setCurrentPage(targetPage);
            setPageSize(size);

            const responseHasMore =
              response.has_more ??
              (list.length === size && (response.next_last_id ?? 0) > 0);
            setHasMore(responseHasMore);

            const nextTokens = [...tokens];
            while (nextTokens.length < targetPage) {
              nextTokens.push(0);
            }
            nextTokens[targetPage - 1] = token;

            const nextToken = response.next_last_id ?? 0;
            if (nextTokens.length <= targetPage) {
              nextTokens.push(nextToken);
            } else {
              nextTokens[targetPage] = nextToken;
            }
            pageTokensRef.current = nextTokens;
          }
        } catch (error: any) {
          if (error.name !== 'AbortError') {
            console.error('加载链接失败:', error);
            message.error('加载链接失败');
          }
        } finally {
          if (!abortControllerRef.current?.signal.aborted) {
            setLoading(false);
          }
        }
      }, debounceDelay);
    },
    [deployFilter, pageSize, searchText, ruleIdFilter, clientIdFilter],
  );

  // 初始加载规则列表（只执行一次）
  useEffect(() => {
    loadRulesList();
    loadDomainsList();
  }, []); // 空依赖，只在组件挂载时执行一次

  // 加载链接数据（初始化 & 搜索/过滤变化时）
  useEffect(() => {
    pageTokensRef.current = [0];
    setCurrentPage(1);
    setHasMore(false);
    loadLinks(1, pageSize, 0, searchText);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadLinks, pageSize]);

  // 处理创建链接（带部署方式）
  const handleCreate = (mode?: 'api' | 'short_link') => {
    setEditingLink(null);
    setCreateMode(mode || null);
    form.resetFields();
    // 如果是特定模式，设置默认值
    if (mode) {
      const defaults: Record<string, any> = {
        deploy_type: mode,
        jump_mode: '1',
        jump_block_mode: mode === 'short_link' ? '2' : '3',
      };
      form.setFieldsValue(defaults);
      if (mode === 'short_link' && !deviceConstantsInitialized) {
        initializeDeviceConstants();
      }
    }
    setModalVisible(true);
  };

  // 处理编辑链接
  const handleEdit = (record: Link) => {
    setEditingLink(record);

    // 使用简化的数据结构
    const initial: any = {
      ...record,
      duplicate_visitor_enabled: record.visitor_control_config?.duplicate_visitor_enabled || false,
      // access_limit_enabled: record.visitor_control_config?.access_limit_enabled || false, // 访问限制功能暂时注释掉
      // access_limit_period: record.visitor_control_config?.access_limit_period || 'day', // 访问限制功能暂时注释掉
      max_duplicate_count: record.visitor_control_config?.duplicate_visitor_config?.max_duplicate_count || 10,
      // 跳转模式配置
      jump_mode: record.jump_mode !== undefined && record.jump_mode !== null ? String(record.jump_mode) : '1',
      jump_block_mode: record.jump_block_mode !== undefined && record.jump_block_mode !== null ? String(record.jump_block_mode) : '3',
      // 保留现有的URL字段
      default_url_input: formatDefaultURLInputValue(record.default_url),
      intercept_url: record.intercept_url || '',
    };

    console.log('编辑链接 - 访客控制配置:', record.visitor_control_config);
    console.log('编辑链接 - 解析后的初始值:', initial);

    form.setFieldsValue(initial);
    if (record.deploy_type === 'short_link' && !deviceConstantsInitialized) {
      initializeDeviceConstants();
    }
    // 规则列表在初始化时已加载，无需重复加载
    setModalVisible(true);
  };

  // 处理复制链接
  const handleCopyLink = async (record: Link) => {
    try {
      // 清空编辑状态，表示这是创建新链接
      setEditingLink(null);

      // 复制链接配置，但清空短链接代码（让后端自动生成）
      const copyData: any = {
        title: `${record.title} - 副本`,
        description: record.description,
        // 不复制 short_code，让后端自动生成包含 clientid 的新短码
        default_url_input: formatDefaultURLInputValue(record.default_url),
        intercept_url: record.intercept_url,
        status: 'audit', // 复制的链接默认为审核状态
        duplicate_visitor_enabled: record.visitor_control_config?.duplicate_visitor_enabled || false,
        // access_limit_enabled: record.visitor_control_config?.access_limit_enabled || false, // 访问限制功能暂时注释掉
        // access_limit_period: record.visitor_control_config?.access_limit_period || 'day', // 访问限制功能暂时注释掉
        max_duplicate_count: record.visitor_control_config?.duplicate_visitor_config?.max_duplicate_count || 10,
        // 复制规则ID，让用户使用相同的规则
        rule_id: record.rule_id,
        // 复制跳转模式配置
        jump_mode: record.jump_mode !== undefined && record.jump_mode !== null ? String(record.jump_mode) : '1',
        jump_block_mode: record.jump_block_mode !== undefined && record.jump_block_mode !== null ? String(record.jump_block_mode) : '3',
        // 复制广告平台
        ad_plat: record.ad_plat || '',
      };

      console.log('复制链接 - 原始配置:', record);
      console.log('复制链接 - 复制后的配置:', copyData);

      form.setFieldsValue(copyData);
      if (record.deploy_type === 'short_link' && !deviceConstantsInitialized) {
        initializeDeviceConstants();
      }
      // 规则列表在初始化时已加载，无需重复加载
      setModalVisible(true);

      message.info('已复制链接配置，请修改标题后保存');
    } catch (error) {
      console.error('复制链接失败:', error);
      message.error('复制链接失败，请重试');
    }
  };

  // 打开 PHP 下载弹窗
  const handleGeneratePHP = (record: Link) => {
    setPhpModalRecord(record);
    setPhpDeployType('standalone');
    setPhpModalVisible(true);
  };

  // 实际下载 PHP 文件
  const handlePhpDownload = async () => {
    if (!phpModalRecord) return;
    setPhpDownloading(true);
    try {
      const response = await linkService.generatePHPFile(phpModalRecord.id, phpDeployType);

      if (response.success && response.data) {
        const blob = new Blob([response.data.file_content], { type: 'text/php' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        message.success('PHP文件已生成并下载');
        setPhpModalVisible(false);
      } else {
        message.error(response.message);
      }
    } catch (error) {
      console.error('生成PHP文件失败:', error);
      message.error('生成PHP文件失败，请重试');
    } finally {
      setPhpDownloading(false);
    }
  };

  // 处理删除链接（带确认弹窗）
  const handleDelete = (record: Link) => {
    Modal.confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>确定要删除链接 <strong>"{record.title}"</strong> 吗？</p>
          <p style={{ color: '#999', fontSize: '12px' }}>删除后该链接将不再显示在列表中。</p>
        </div>
      ),
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await linkService.deleteLink(record.id);
          message.success('删除成功');
          loadLinks(currentPage, pageSize, pageTokensRef.current[Math.max(currentPage - 1, 0)] ?? 0, searchText);
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  // 处理状态切换
  const handleToggleStatus = async (record: Link) => {
    try {
      const result = await linkService.toggleLink(record.id);
      message.success('状态切换成功');
      loadLinks(currentPage, pageSize, pageTokensRef.current[Math.max(currentPage - 1, 0)] ?? 0, searchText);
    } catch (error) {
      console.error('状态切换失败:', error);
      message.error('状态切换失败');
    }
  };

  // 处理状态变更
  const handleStatusChange = async (record: Link, newStatus: string) => {
    const currentStatus = getStatusConfig(record.status);
    const newStatusInfo = getStatusConfig(newStatus);

    // 如果状态没有变化，直接返回
    if (record.status === newStatus) {
      return;
    }

    Modal.confirm({
      title: '确认状态变更',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            确定要将链接 <strong>"{record.title}"</strong> 的状态进行以下变更吗？
          </p>
          <div style={{
            background: '#f5f5f5',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px'
          }}>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>当前状态：</span>
              <span style={{
                color: record.status === 'green' ? '#52c41a' :
                  record.status === 'audit' ? '#faad14' : '#ff4d4f',
                fontWeight: '500'
              }}>
                {currentStatus.text}
              </span>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>目标状态：</span>
              <span style={{
                color: newStatus === 'green' ? '#52c41a' :
                  newStatus === 'audit' ? '#faad14' : '#ff4d4f',
                fontWeight: '500'
              }}>
                {newStatusInfo.text}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              {newStatusInfo.description}
            </div>
          </div>
          <p style={{ color: '#666', fontSize: '12px' }}>
            状态变更后，链接的访问行为将立即生效。
          </p>
        </div>
      ),
      okText: '确认变更',
      cancelText: '取消',
      okButtonProps: {
        style: {
          backgroundColor: newStatus === 'green' ? '#52c41a' :
            newStatus === 'audit' ? '#faad14' : '#ff4d4f',
          borderColor: newStatus === 'green' ? '#52c41a' :
            newStatus === 'audit' ? '#faad14' : '#ff4d4f',
          marginRight: '8px'
        }
      },
      cancelButtonProps: {
        style: {
          marginLeft: '0'
        }
      },
      footer: (_, { OkBtn, CancelBtn }) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <OkBtn />
          <CancelBtn />
        </div>
      ),
      width: 480,
      onOk: async () => {
        try {
          await linkService.toggleLinkStatus(record.id, newStatus as 'audit' | 'green' | 'block');
          message.success(`状态已成功变更为"${newStatusInfo.text}"`);
          loadLinks(currentPage, pageSize, pageTokensRef.current[Math.max(currentPage - 1, 0)] ?? 0, searchText);
        } catch (error) {
          console.error('状态更新失败:', error);
          message.error('状态更新失败，请重试');
        }
      }
    });
  };

  // 处理规则配置
  const handleRulesConfig = async (record: Link) => {
    setEditingLink(record);
    setRuleConfigMode('view');

    // 若无 rule_id，提示用户先选择/新建规则
    if (!record || !('rule_id' in record) || !(record as any).rule_id) {
      message.warning('该链接尚未绑定规则，请先在"规则选择"处选择或新建规则');
      return;
    }

    try {
      // 拉取规则详情
      const detail: any = await ruleService.getRuleDetail((record as any).rule_id);
      const ruleConfig = detail?.rule_config || {};

      // 8个固定子规则，根据配置内容计算 isActive
      const subRules: RuleConfigItem[] = RULE_TYPES.map(r => ({
        id: Math.random(),
        type: r.type,
        name: r.name,
        isActive: false, // 将在下面根据配置内容计算
        config: ruleConfig[r.type] || {}
      }));

      // 注入真实规则ID，便于后续局部保存
      subRules.forEach(r => {
        (r as any).rule_id = (record as any).rule_id;
      });

      // 根据配置内容计算 isActive
      const checkRuleHasValidConfig = (ruleType: string, config: any) => {
        switch (ruleType) {
          case 'ip_blacklist':
            return (config.blacklist && config.blacklist.length > 0) ||
              (config.whitelist && config.whitelist.length > 0) ||
              (config.allowedIPs && config.allowedIPs.length > 0) ||
              (config.blockedIPs && config.blockedIPs.length > 0);

          case 'url_referer':
            return (config.refererList && config.refererList.length > 0);

          case 'geography':
            return (config.allowedCountries && config.allowedCountries.length > 0) ||
              (config.blockedCountries && config.blockedCountries.length > 0);

          case 'network':
            // 检查网络安全检测的各种配置
            // 如果字段存在且为 true，或者有数组配置，则认为已配置
            return config.proxyDetection === true ||
              config.datacenterDetection === true ||
              config.vpnDetection === true ||
              config.satelliteDetection === true ||
              config.mobileNetworkDetection === true ||
              config.icloudProxyDetection === true ||
              (config.blockedNetworks && config.blockedNetworks.length > 0) ||
              (config.allowedNetworks && config.allowedNetworks.length > 0) ||
              (config.riskLevels && config.riskLevels.length > 0) ||
              // 检查是否有任何网络检测字段被明确设置（即使是 false 也说明用户配置过）
              (config.proxyDetection !== undefined) ||
              (config.datacenterDetection !== undefined) ||
              (config.vpnDetection !== undefined) ||
              (config.satelliteDetection !== undefined) ||
              (config.mobileNetworkDetection !== undefined) ||
              (config.icloudProxyDetection !== undefined);

          case 'device':
            const deviceConfigKeys = [
              'deviceTypeAllow', 'deviceTypeBlock', 'browserAllow', 'browserBlock',
              'clientTypeAllow', 'clientTypeBlock', 'mobileAppAllow', 'mobileAppBlock',
              'systemAllow', 'systemBlock', 'brandAllow', 'brandBlock'
            ];
            return deviceConfigKeys.some(key =>
              config[key] && Array.isArray(config[key]) && config[key].length > 0
            );

          case 'language':
            return (config.allowedLanguages && config.allowedLanguages.length > 0) ||
              (config.blockedLanguages && config.blockedLanguages.length > 0);

          case 'utm':
            const utmConfigKeys = [
              'utmSourceAllowed', 'utmSourceBlocked', 'utmMediumAllowed', 'utmMediumBlocked',
              'utmCampaignAllowed', 'utmCampaignBlocked', 'utmTermAllowed', 'utmTermBlocked',
              'utmContentAllowed', 'utmContentBlocked'
            ];
            return utmConfigKeys.some(key =>
              config[key] && Array.isArray(config[key]) && config[key].length > 0
            );

          case 'crawler':
            return (config.crawlerAllowed && config.crawlerAllowed.length > 0) ||
              (config.crawlerBlocked && config.crawlerBlocked.length > 0) ||
              config.searchEngineCrawler === true ||
              config.blockMaliciousCrawler === true;

          default:
            return false;
        }
      };

      // 根据配置内容计算 isActive
      subRules.forEach(rule => {
        rule.isActive = checkRuleHasValidConfig(rule.type, rule.config);
      });

      setCurrentLinkRules(subRules);
      setRulesModalVisible(true);
    } catch (e) {
      message.error('加载规则详情失败');
    }
  };

  // 处理保存链接
  const handleSave = async (values: any) => {
    try {
      // 检查是否选择了规则
      if (!values.rule_id) {
        message.error('请先选择拦截规则');
        return;
      }

      // 构建访客控制配置（只保留重复访客控制）
      const visitorControlConfig = {
        duplicate_visitor_enabled: values.duplicate_visitor_enabled || false,
        access_limit_enabled: false, // 访问限制功能已隐藏
        access_limit_period: 'day',
        duplicate_visitor_config: {
          max_duplicate_count: values.max_duplicate_count || 1,
          time_limit: 24 // 24小时限制
        },
        access_limit_config: {
          limit_by_ip: true,
          ip_access_limit: 1000 // 设置默认值
        }
      };

      console.log('保存链接 - 表单值:', values);
      console.log('保存链接 - 构建的访客控制配置:', visitorControlConfig);

      // 构建最终的数据对象
      const linkData = {
        ...values,
        jump_mode: values.jump_mode !== undefined && values.jump_mode !== null ? Number(values.jump_mode) : 1,
        jump_block_mode: values.jump_block_mode !== undefined && values.jump_block_mode !== null ? Number(values.jump_block_mode) : 3,
        visitor_control_config: visitorControlConfig
      };

      const defaultURLList = parseDefaultURLInputValue(values.default_url_input);
      if (defaultURLList.length === 0) {
        message.error('请至少填写一个跳转URL地址');
        return;
      }
      linkData.default_url = defaultURLList.join(',');

      console.log('保存链接 - 最终提交数据:', linkData);
      console.log('保存链接 - visitor_control_config 字段:', JSON.stringify(linkData.visitor_control_config, null, 2));

      // 移除临时的表单字段（在构建 visitor_control_config 之后）
      delete linkData.duplicate_visitor_enabled;
      // delete linkData.access_limit_enabled; // 访问限制功能暂时注释掉
      // delete linkData.access_limit_period; // 访问限制功能暂时注释掉
      delete linkData.max_duplicate_count;

      delete linkData.default_url_input;

      // 创建链接时移除 short_code 字段，让后端自动生成
      if (!editingLink) {
        delete linkData.short_code;
      }

      if (editingLink) {
        await linkService.updateLink({ id: editingLink.id, ...linkData });
        message.success('更新成功');
      } else {
        await linkService.createLink(linkData);
        message.success('创建成功');
      }
      setModalVisible(false);
      pageTokensRef.current = [0];
      setCurrentPage(1);
      setHasMore(false);
      loadLinks(1, pageSize, 0, searchText);
    } catch (error) {
      console.error('保存链接失败:', error);
      handleApiError(error, '保存失败');
    }
  };


  // 表格列定义
  const columns = [
    {
      title: '链接标题',
      dataIndex: 'title',
      key: 'title',
      width: 150,
      render: (text: string) => (
        <div style={{
          fontWeight: 600,
          color: 'var(--color-text-primary)',
          fontSize: '14px',
          letterSpacing: '-0.2px'
        }}>
          {text}
        </div>
      ),
    },
    {
      title: '短链码',
      dataIndex: 'short_code',
      key: 'short_code',
      width: 100,
      render: (shortCode: string) => (
        <div style={{
          fontSize: '12px',
          color: 'var(--color-text-secondary)',
          fontFamily: 'monospace',
          background: 'var(--color-bg-secondary)',
          padding: '4px 8px',
          borderRadius: '6px',
          width: 'fit-content'
        }}>
          {shortCode}
        </div>
      ),
    },
    {
      title: '推广链接',
      key: 'promotion_link',
      width: 300,
      render: (_: any, record: Link) => {
        const isShortLinkMode = record.deploy_type === 'short_link';
        const isApiMode = !record.deploy_type || record.deploy_type === 'api';

        // 短链模式：显示完整URL
        if (isShortLinkMode) {
          const promotionLink = record.domain_name
            ? `https://${record.domain_name}/${record.short_code}`
            : record.short_code;

          return (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'var(--color-bg-secondary)',
              padding: '6px 12px',
              borderRadius: '6px',
              width: 'fit-content',
              maxWidth: '100%'
            }}>
              <LinkOutlined style={{ fontSize: '12px', color: 'var(--color-primary)', flexShrink: 0 }} />
              <Text
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '200px'
                }}
                title={promotionLink}
              >
                {promotionLink}
              </Text>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(promotionLink);
                  message.success('推广链接已复制');
                }}
                style={{
                  padding: '2px 6px',
                  height: 'auto',
                  fontSize: '12px',
                  color: 'var(--color-primary)',
                  flexShrink: 0
                }}
              >
                复制推广链接
              </Button>
            </div>
          );
        }

        // API/PHP模式：显示服务器域名，无复制按钮
        if (isApiMode) {
          const serverDomain = "你的服务器域名";

          return (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'var(--color-bg-secondary)',
              padding: '6px 12px',
              borderRadius: '6px',
              width: 'fit-content',
              maxWidth: '100%'
            }}>
              <LinkOutlined style={{ fontSize: '12px', color: 'var(--color-text-secondary)', flexShrink: 0 }} />
              <Text
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '200px'
                }}
                title={serverDomain}
              >
                {serverDomain}
              </Text>
            </div>
          );
        }

        return null;
      },
    },
    ...(isAdmin
      ? [
        {
          title: '用户ID',
          dataIndex: 'user_id',
          key: 'user_id',
          width: 90,
          render: (value: number | undefined) => (
            <Text
              style={{
                fontFamily: 'monospace',
                fontSize: '12px',
                color: 'var(--color-text-secondary)',
              }}
            >
              {value ?? '-'}
            </Text>
          ),
        },
        {
          title: '客户ID',
          dataIndex: 'client_id',
          key: 'client_id',
          width: 90,
          render: (value: number | undefined) => (
            <Text
              style={{
                fontFamily: 'monospace',
                fontSize: '12px',
                color: 'var(--color-text-secondary)',
              }}
            >
              {value ?? '-'}
            </Text>
          ),
        },
      ]
      : []),
    {
      title: '部署模式',
      dataIndex: 'deploy_type',
      key: 'deploy_type',
      width: 100,
      render: (deployType: string | undefined, record: Link) => {
        const type = deployType || 'api';
        return (
          <Tag
            color={type === 'short_link' ? 'blue' : 'purple'}
            icon={type === 'short_link' ? <LinkOutlined /> : <SettingOutlined />}
          >
            {type === 'short_link' ? '短链模式' : 'API/PHP模式'}
          </Tag>
        );
      },
    },
    {
      title: '跳转配置',
      key: 'redirect_config',
      width: 280,
      render: (text: any, record: Link) => {
        const jumpMode = record.jump_mode || 1;
        const jumpBlockMode = record.jump_block_mode || 3;

        const getModeLabel = (mode: number, isBlock: boolean = false) => {
          switch (mode) {
            case 1:
              return isBlock ? '拦截跳转' : '正常跳转';
            case 2:
              return isBlock ? '拦截页面' : '正常页面';
            case 3:
              return '404 页面';
            default:
              return '未知';
          }
        };

        const getModeColor = (mode: number, isBlock: boolean = false) => {
          switch (mode) {
            case 1:
              return isBlock ? 'orange' : 'green';
            case 2:
              return isBlock ? 'purple' : 'blue';
            case 3:
              return 'red';
            default:
              return 'default';
          }
        };

        const defaultSummary = summarizeDefaultURLs(record.default_url);

        return (
          <div style={{ lineHeight: '1.8' }}>
            {/* 正常访问配置 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <Tag
                color="success"
                style={{
                  margin: 0,
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 500,
                  padding: '2px 8px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  color: 'var(--color-success)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  minWidth: '50px',
                  textAlign: 'center'
                }}
              >
                正常
              </Tag>
              <Tag
                color={getModeColor(jumpMode)}
                style={{
                  margin: 0,
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 500,
                  padding: '2px 8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  minWidth: '60px',
                  justifyContent: 'center'
                }}
              >
                {getModeLabel(jumpMode)}
              </Tag>
              {defaultSummary.first ? (
                <Space size={4} wrap>
                  <Tooltip title={defaultSummary.tooltip || undefined} placement="topLeft">
                    <a
                      href={defaultSummary.first}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '12px',
                        maxWidth: '160px',
                        color: 'var(--color-text-secondary)',
                        textDecoration: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                        transition: 'color 0.2s'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-primary)')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-text-secondary)')}
                    >
                      {defaultSummary.first}
                    </a>
                  </Tooltip>
                  {defaultSummary.others > 0 && (
                    <Tag
                      color="processing"
                      style={{
                        margin: 0,
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 500,
                        padding: '0 6px'
                      }}
                    >
                      +{defaultSummary.others}
                    </Tag>
                  )}
                </Space>
              ) : (
                <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>未配置</span>
              )}
            </div>

            {/* 拦截访问配置 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Tag
                color="error"
                style={{
                  margin: 0,
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 500,
                  padding: '2px 8px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: 'var(--color-error)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  minWidth: '50px',
                  textAlign: 'center'
                }}
              >
                拦截
              </Tag>
              <Tag
                color={getModeColor(jumpBlockMode, true)}
                style={{
                  margin: 0,
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 500,
                  padding: '2px 8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  minWidth: '60px',
                  justifyContent: 'center'
                }}
              >
                {getModeLabel(jumpBlockMode, true)}
              </Tag>
              {jumpBlockMode !== 3 && (
                <Tooltip title={record.intercept_url} placement="topLeft">
                  <a
                    href={record.intercept_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '12px',
                      maxWidth: '160px',
                      color: 'var(--color-text-secondary)',
                      textDecoration: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-secondary)'}
                  >
                    {record.intercept_url}
                  </a>
                </Tooltip>
              )}
              {jumpBlockMode === 3 && (
                <span style={{
                  fontSize: '12px',
                  color: 'var(--color-text-tertiary)',
                  fontStyle: 'italic'
                }}>
                  自动404页面
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: '规则配置',
      dataIndex: 'rule_id',
      key: 'rule_id',
      width: 100,
      render: (ruleId: number, record: Link) => (
        <div>
          {ruleId ? (
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <Tag
                icon={<SettingOutlined />}
                color="blue"
                style={{
                  margin: 0,
                  borderRadius: '6px',
                  fontSize: '11px',
                  padding: '4px 10px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: 'rgba(99, 102, 241, 0.1)',
                  color: 'var(--color-primary)',
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                  fontWeight: 500
                }}
              >
                {record.rule_name || `规则 #${ruleId}`}
              </Tag>
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleRulesConfig(record)}
                style={{
                  padding: 0,
                  height: 'auto',
                  fontSize: '12px',
                  color: 'var(--color-primary)',
                  fontWeight: 500
                }}
              >
                查看详情
              </Button>
            </Space>
          ) : (
            <Tooltip title="未配置拦截规则">
              <Tag
                color="default"
                style={{
                  borderRadius: '6px',
                  fontSize: '11px',
                  padding: '4px 12px',
                  background: 'var(--color-bg-secondary)',
                  color: 'var(--color-text-tertiary)',
                  border: '1px solid var(--color-border-light)'
                }}
              >
                未配置
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: '广告平台',
      dataIndex: 'ad_plat',
      key: 'ad_plat',
      width: 100,
      render: (adPlat: string | undefined) => {
        const platformConfig: Record<string, { label: string; color: string }> = {
          facebook: { label: 'Facebook', color: 'blue' },
          google: { label: 'Google', color: 'green' },
          tiktok: { label: 'TikTok', color: 'magenta' },
          twitter: { label: 'Twitter', color: 'cyan' },
          bing: { label: 'Bing', color: 'orange' },
          other: { label: '其他', color: 'default' },
        };

        if (!adPlat) {
          return (
            <Tag
              color="default"
              style={{
                borderRadius: '6px',
                fontSize: '11px',
                padding: '2px 8px',
                background: 'var(--color-bg-secondary)',
                color: 'var(--color-text-tertiary)',
                border: '1px solid var(--color-border-light)'
              }}
            >
              未设置
            </Tag>
          );
        }

        const config = platformConfig[adPlat] || { label: adPlat, color: 'default' };
        return (
          <Tag
            color={config.color}
            style={{
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: 500,
              padding: '2px 8px'
            }}
          >
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (status: string, record: Link) => {
        const statusInfo = getStatusConfig(status);

        const statusIconMap = {
          green: <CheckCircleOutlined />,
          audit: <ExclamationCircleOutlined />,
          block: <CloseCircleOutlined />
        };

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
            {/* 状态切换按钮组 */}
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {/* 绿灯按钮 */}
              <Tooltip title={`${LINK_STATUS_CONFIG.green.text}：${LINK_STATUS_CONFIG.green.description}`}>
                <button
                  onClick={() => {
                    if (status !== 'green') {
                      handleStatusChange(record, 'green');
                    }
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '4px',
                    border: 'none',
                    cursor: status === 'green' ? 'default' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    backgroundColor: status === 'green' ? '#52c41a' : '#f5f5f5',
                    color: status === 'green' ? '#fff' : '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    minWidth: '60px',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (status !== 'green') {
                      e.currentTarget.style.backgroundColor = '#52c41a';
                      e.currentTarget.style.color = '#fff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (status !== 'green') {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                      e.currentTarget.style.color = '#666';
                    }
                  }}
                >
                  <CheckCircleOutlined />
                  <span>绿灯</span>
                </button>
              </Tooltip>

              {/* 审核按钮 */}
              <Tooltip title={`${LINK_STATUS_CONFIG.audit.text}：${LINK_STATUS_CONFIG.audit.description}`}>
                <button
                  onClick={() => {
                    if (status !== 'audit') {
                      handleStatusChange(record, 'audit');
                    }
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '4px',
                    border: 'none',
                    cursor: status === 'audit' ? 'default' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    backgroundColor: status === 'audit' ? '#faad14' : '#f5f5f5',
                    color: status === 'audit' ? '#fff' : '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    minWidth: '60px',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (status !== 'audit') {
                      e.currentTarget.style.backgroundColor = '#faad14';
                      e.currentTarget.style.color = '#fff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (status !== 'audit') {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                      e.currentTarget.style.color = '#666';
                    }
                  }}
                >
                  <ExclamationCircleOutlined />
                  <span>审核</span>
                </button>
              </Tooltip>

              {/* 拦截按钮 */}
              <Tooltip title={`${LINK_STATUS_CONFIG.block.text}：${LINK_STATUS_CONFIG.block.description}`}>
                <button
                  onClick={() => {
                    if (status !== 'block') {
                      handleStatusChange(record, 'block');
                    }
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '4px',
                    border: 'none',
                    cursor: status === 'block' ? 'default' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    backgroundColor: status === 'block' ? '#ff4d4f' : '#f5f5f5',
                    color: status === 'block' ? '#fff' : '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    minWidth: '60px',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (status !== 'block') {
                      e.currentTarget.style.backgroundColor = '#ff4d4f';
                      e.currentTarget.style.color = '#fff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (status !== 'block') {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                      e.currentTarget.style.color = '#666';
                    }
                  }}
                >
                  <CloseCircleOutlined />
                  <span>斗篷</span>
                </button>
              </Tooltip>
            </div>
          </div>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (createdAt: string) => (
        <span>{new Date(createdAt).toLocaleString()}</span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: Link) => (
        <Space
          direction="vertical"
          size={6}
          style={{ width: '100%' }}
        >
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{
              padding: 0,
              height: 'auto',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: 'var(--color-primary)',
              fontWeight: 500,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateX(2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopyLink(record)}
            style={{
              padding: 0,
              height: 'auto',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: 'var(--color-info)',
              fontWeight: 500,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateX(2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            复制
          </Button>
          <Button
            type="link"
            size="small"
            icon={<UserOutlined />}
            onClick={() => navigate(`/visitors?link_id=${record.id}`)}
            style={{
              padding: 0,
              height: 'auto',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: 'var(--color-warning)',
              fontWeight: 500,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateX(2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            访客记录
          </Button>
          <Button
            type="link"
            size="small"
            icon={<BarChartOutlined />}
            onClick={() => navigate(`/statistics?short_code=${record.short_code}`)}
            style={{
              padding: 0,
              height: 'auto',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: '#722ed1',
              fontWeight: 500,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateX(2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            访客统计
          </Button>
          {/* 只在API部署模式下显示生成中间件按钮 */}
          {(record.deploy_type === 'api' || !record.deploy_type) && (
            <Button
              type="link"
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => handleGeneratePHP(record)}
              style={{
                padding: 0,
                height: 'auto',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: 'var(--color-success)',
                fontWeight: 500,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateX(2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              生成PHP脚本
            </Button>
          )}
          <Button
            type="link"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            style={{
              padding: 0,
              height: 'auto',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: 'var(--color-error)',
              fontWeight: 500,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateX(2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 顶部滚动条同步 - 查找表格滚动容器（可能是 .ant-table-body 或 .ant-table-content）
  const linkTableWrapperRef = useRef<HTMLDivElement>(null);
  const linkTopScrollRef = useRef<HTMLDivElement>(null);
  const [linkTableScrollWidth, setLinkTableScrollWidth] = useState(1650);

  const getTableScrollContainer = useCallback(() => {
    const wrapper = linkTableWrapperRef.current;
    if (!wrapper) return null;
    // 优先查找 .ant-table-body，没有则查找 .ant-table-content
    return (wrapper.querySelector('.ant-table-body') || wrapper.querySelector('.ant-table-content')) as HTMLDivElement | null;
  }, []);

  useEffect(() => {
    const tableContainer = getTableScrollContainer();
    if (tableContainer) {
      const updateWidth = () => {
        const scrollWidth = tableContainer.scrollWidth;
        if (scrollWidth > 0) {
          setLinkTableScrollWidth(scrollWidth);
        }
      };
      updateWidth();
      const observer = new ResizeObserver(updateWidth);
      observer.observe(tableContainer);
      return () => observer.disconnect();
    }
  }, [links, getTableScrollContainer]);

  const handleLinkTopScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const tableContainer = getTableScrollContainer();
    if (tableContainer) {
      tableContainer.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleLinkTableScroll = useCallback(() => {
    const tableContainer = getTableScrollContainer();
    if (tableContainer && linkTopScrollRef.current) {
      linkTopScrollRef.current.scrollLeft = tableContainer.scrollLeft;
    }
  }, [getTableScrollContainer]);

  useEffect(() => {
    const tableContainer = getTableScrollContainer();
    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleLinkTableScroll);
      return () => tableContainer.removeEventListener('scroll', handleLinkTableScroll);
    }
  }, [links, handleLinkTableScroll, getTableScrollContainer]);

  return (
    <div style={{ padding: '24px', background: 'var(--color-bg-tertiary)', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{
        background: 'var(--color-bg-primary)',
        padding: '24px 32px',
        borderRadius: 'var(--border-radius-lg)',
        marginBottom: '24px',
        boxShadow: 'var(--box-shadow-sm)',
        border: '1px solid var(--color-border-light)'
      }}>
        <Title level={2} style={{ margin: 0, marginBottom: '8px', fontSize: '28px', fontWeight: 700 }}>链接管理</Title>
        <Text type="secondary" style={{ fontSize: '14px' }}>管理所有短链接的配置和跳转规则</Text>
      </div>

      <Card
        bordered={false}
        style={{
          borderRadius: 'var(--border-radius-lg)',
          boxShadow: 'var(--box-shadow-sm)',
          border: '1px solid var(--color-border-light)'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <div style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <Space size="middle" wrap>
            <Input
              placeholder="搜索标题或短码"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onPressEnter={() => {
                setSearchText(searchInput);
                setRuleIdFilter(ruleIdInput);
                setClientIdFilter(clientIdInput);
              }}
              style={{
                width: 200,
                borderRadius: 'var(--border-radius-base)'
              }}
              prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
              allowClear
              onClear={() => {
                setSearchInput('');
                setSearchText('');
              }}
            />
            <Input
              placeholder="规则ID"
              value={ruleIdInput}
              onChange={(e) => setRuleIdInput(e.target.value)}
              onPressEnter={() => {
                setSearchText(searchInput);
                setRuleIdFilter(ruleIdInput);
                setClientIdFilter(clientIdInput);
              }}
              style={{
                width: 120,
                borderRadius: 'var(--border-radius-base)'
              }}
              allowClear
              onClear={() => {
                setRuleIdInput('');
                setRuleIdFilter('');
              }}
            />
            <Input
              placeholder="客户ID"
              value={clientIdInput}
              onChange={(e) => setClientIdInput(e.target.value)}
              onPressEnter={() => {
                setSearchText(searchInput);
                setRuleIdFilter(ruleIdInput);
                setClientIdFilter(clientIdInput);
              }}
              style={{
                width: 120,
                borderRadius: 'var(--border-radius-base)'
              }}
              allowClear
              onClear={() => {
                setClientIdInput('');
                setClientIdFilter('');
              }}
            />
            <Space size="small" style={{ alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>部署模式：</Text>
              <Select
                value={deployFilter}
                onChange={(value) => {
                  setDeployFilter(value);
                }}
                style={{ width: 120 }}
                placeholder="选择部署模式"
              >
                <Option value="all">全部</Option>
                <Option value="api">API/PHP模式</Option>
                <Option value="short_link">短链模式</Option>
              </Select>
            </Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => {
                setSearchText(searchInput);
                setRuleIdFilter(ruleIdInput);
                setClientIdFilter(clientIdInput);
                pageTokensRef.current = [0];
                setCurrentPage(1);
                setHasMore(false);
              }}
              style={{ borderRadius: 'var(--border-radius-base)' }}
            >
              搜索
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setSearchInput('');
                setRuleIdInput('');
                setClientIdInput('');
                setSearchText('');
                setRuleIdFilter('');
                setClientIdFilter('');
                setDeployFilter('all');
                pageTokensRef.current = [0];
                setCurrentPage(1);
                setHasMore(false);
              }}
              style={{ borderRadius: 'var(--border-radius-base)' }}
            >
              重置
            </Button>
          </Space>
          <Space size="middle">
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={() => handleCreate('short_link')}
              style={{
                borderRadius: 'var(--border-radius-base)',
                height: '38px',
                padding: '0 20px',
                fontWeight: 500,
                boxShadow: '0 2px 0 rgba(99, 102, 241, 0.1)'
              }}
            >
              创建短链
            </Button>
            <Button
              type="default"
              icon={<SettingOutlined />}
              onClick={() => handleCreate('api')}
              style={{
                borderRadius: 'var(--border-radius-base)',
                height: '38px',
                padding: '0 20px',
                fontWeight: 500
              }}
            >
              创建API/PHP脚本
            </Button>
          </Space>
        </div>

        {/* 顶部滚动条 */}
        <div
          ref={linkTopScrollRef}
          onScroll={handleLinkTopScroll}
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            height: 16,
            marginBottom: 8,
            background: '#fafafa',
            borderRadius: 4
          }}
        >
          <div style={{ width: linkTableScrollWidth, height: 1 }} />
        </div>
        <div ref={linkTableWrapperRef}>
          <Table
            columns={columns}
            dataSource={links}
            rowKey="id"
            loading={loading}
            size="middle"
            style={{
              borderRadius: 'var(--border-radius-lg)',
              overflow: 'hidden'
            }}
            rowClassName={(record, index) =>
              index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
            }
            pagination={false}
            scroll={{ x: 1650 }}
          />
        </div>

        <div
          style={{
            marginTop: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Space>
            <Text type="secondary">共 {total} 条记录</Text>
            <Text type="secondary">第 {currentPage} 页</Text>
            <Text type="secondary">每页 {pageSize} 条</Text>
          </Space>
          <Space>
            <Button
              disabled={currentPage <= 1 || loading}
              onClick={() => {
                const prevToken = pageTokensRef.current[Math.max(currentPage - 2, 0)] ?? 0;
                loadLinks(currentPage - 1, pageSize, prevToken, searchText);
              }}
            >
              上一页
            </Button>
            <Button
              type="primary"
              disabled={!hasMore || loading}
              onClick={() => {
                const nextToken = pageTokensRef.current[currentPage] ?? 0;
                loadLinks(currentPage + 1, pageSize, nextToken, searchText);
              }}
            >
              下一页
            </Button>
          </Space>
        </div>
      </Card>

      {/* 创建/编辑链接弹窗 */}
      <Modal
        title={
          <div style={{ fontSize: '18px', fontWeight: 600 }}>
            {editingLink ? '编辑链接' : '创建链接'}
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={800}
        okText="保存"
        cancelText="取消"
        okButtonProps={{
          style: {
            borderRadius: 'var(--border-radius-base)',
            fontWeight: 500
          }
        }}
        cancelButtonProps={{
          style: {
            borderRadius: 'var(--border-radius-base)'
          }
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="title"
            label="链接标题"
            rules={[{ required: true, message: '请输入链接标题' }]}
          >
            <Input placeholder="请输入链接标题" />
          </Form.Item>

          <Form.Item
            name="description"
            label="链接描述"
          >
            <Input.TextArea placeholder="请输入链接描述" rows={3} />
          </Form.Item>

          {/* 广告平台选择 */}
          <Form.Item
            name="ad_plat"
            label="广告平台"
            tooltip="选择此链接投放的广告平台，方便后续数据统计和分析"
          >
            <Select placeholder="请选择广告平台" allowClear>
              <Option value="facebook">Facebook</Option>
              <Option value="google">Google</Option>
              <Option value="tiktok">TikTok</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>

          {/* 短链接代码由后端自动生成，包含客户ID前缀 */}
          {editingLink && (
            <Form.Item
              label="短链接代码"
            >
              <Input
                value={editingLink.short_code}
                disabled
                addonAfter={
                  <Button
                    type="link"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/${editingLink.short_code}`);
                      message.success('短链接已复制到剪贴板');
                    }}
                  >
                    复制链接
                  </Button>
                }
              />
            </Form.Item>
          )}

          <Form.Item
            name="rule_id"
            label="规则选择"
            rules={[{ required: true, message: '请选择拦截规则' }]}
          >
            <Select
              placeholder={rulesList.length === 0 ? "暂无规则，请先创建规则" : "请选择拦截规则"}
              allowClear
              loading={rulesLoading}
              showSearch
              optionFilterProp="children"
              disabled={rulesList.length === 0}
              filterOption={(input, option) => {
                const children = option?.children as unknown as string;
                return children?.toLowerCase().includes(input.toLowerCase()) || false;
              }}
              dropdownRender={(menu) => (
                <div>
                  {rulesList.length === 0 ? (
                    <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <ExclamationCircleOutlined style={{ fontSize: '24px', color: '#faad14' }} />
                      </div>
                      <div style={{ marginBottom: '12px', fontSize: '14px' }}>
                        暂无拦截规则
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                        请先创建拦截规则，然后再创建链接
                      </div>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                          setModalVisible(false);
                          navigate('/rules');
                        }}
                        style={{ borderRadius: '4px' }}
                      >
                        去创建规则
                      </Button>
                    </div>
                  ) : (
                    <>
                      {menu}
                      <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0' }}>
                        {rulesHasMore && (
                          <Button
                            type="link"
                            size="small"
                            loading={rulesLoading}
                            onClick={(e) => {
                              e.stopPropagation();
                              loadMoreRules();
                            }}
                            style={{ width: '100%', textAlign: 'center', marginBottom: '4px' }}
                          >
                            加载更多规则...
                          </Button>
                        )}
                        <Button
                          type="link"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setModalVisible(false);
                            navigate('/rules');
                          }}
                          style={{ width: '100%', textAlign: 'center' }}
                        >
                          创建新规则
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            >
              {rulesList.map(rule => (
                <Option key={rule.id} value={rule.id}>
                  {rule.name || `规则 #${rule.id}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* 部署方式：隐藏选择器，根据创建入口自动设置 */}
          <Form.Item name="deploy_type" hidden>
            <Input />
          </Form.Item>

          {/* 根据部署方式显示相应配置 */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.deploy_type !== currentValues.deploy_type}
          >
            {({ getFieldValue }) => {
              const deployType = getFieldValue('deploy_type') || 'api';

              if (deployType === 'short_link') {
                return (
                  <>
                    <Divider orientation="left" style={{ margin: '24px 0 16px 0' }}>
                      <Space>
                        <LinkOutlined />
                        <Text strong>短链配置</Text>
                      </Space>
                    </Divider>
                    <Form.Item
                      name="domain_id"
                      label="选择域名"
                      tooltip={editingLink ? "编辑模式下域名不可修改" : "选择用于生成短链的域名"}
                      rules={[{ required: true, message: '请选择域名' }]}
                    >
                      <Select
                        placeholder="请选择域名"
                        showSearch
                        disabled={!!editingLink}
                        loading={domainsLoading}
                        filterOption={(input, option) =>
                          (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {domainsList.map(domain => (
                          <Option key={domain.id} value={domain.id}>
                            {domain.domain_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Alert
                      message="短链模式说明"
                      description="此链接将使用选择的域名生成短链。访问格式：https://{域名}/{短码}"
                      type="info"
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                  </>
                );
              }

              if (deployType === 'api') {
                return (
                  <>
                    <Divider orientation="left" style={{ margin: '24px 0 16px 0' }}>
                      <Space>
                        <SettingOutlined />
                        <Text strong>API/PHP部署</Text>
                      </Space>
                    </Divider>
                    <Alert
                      message="API/PHP部署说明"
                      description="此链接通过API调用或PHP文件部署"
                      type="info"
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                  </>
                );
              }

              return null;
            }}
          </Form.Item>

          {/* 跳转模式配置 */}
          <Divider orientation="left" style={{ margin: '24px 0 16px 0' }}>
            <Text strong>跳转模式配置</Text>
          </Divider>

          <Alert
            message="跳转模式说明"
            description={
              <div>
                <p><strong>正常访问模式</strong>：当访客符合规则时的处理方式</p>
                <p><strong>拦截访问模式</strong>：当访客被拦截时的处理方式</p>
                <p><strong>兼容性</strong>：支持302重定向、系统404响应，以及（API/PHP部署下的）本地文件渲染</p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="jump_mode"
                label="正常访问模式"
                tooltip="当访客符合规则时的跳转方式"
                initialValue="1"
              >
                <Select placeholder="请选择正常访问模式">
                  <Option value="1" label="跳转到URL">
                    <Space>
                      <span>跳转到URL</span>
                      <Tag color="blue">推荐</Tag>
                    </Space>
                  </Option>
                  {deployTypeWatch !== 'short_link' && (
                    <Option value="2" label="显示本地HTML文件">
                      <Space>
                        <span>显示本地HTML文件</span>
                        <Tag color="orange">模板</Tag>
                      </Space>
                    </Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="jump_block_mode"
                label="拦截访问模式"
                tooltip="当访客被拦截时的处理方式"
                initialValue="3"
              >
                <Select placeholder="请选择拦截访问模式">
                  <Option value="1" label="跳转到拦截URL">
                    <Space>
                      <span>跳转到拦截URL</span>
                      <Tag color="red">通用</Tag>
                    </Space>
                  </Option>
                  <Option
                    value="2"
                    label="显示本地HTML文件"
                  >
                    <Space>
                      <span>显示本地HTML文件</span>
                      <Tag color="orange">{deployTypeWatch === 'short_link' ? '模板' : 'API调用'}</Tag>
                    </Space>
                  </Option>
                  <Option value="3" label="返回404错误">
                    <Space>
                      <span>返回404错误</span>
                      <Tag color="purple">系统响应</Tag>
                    </Space>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 跳转地址配置 - 根据模式动态显示 */}
          <Form.Item shouldUpdate={(prevValues, currentValues) =>
            prevValues.jump_mode !== currentValues.jump_mode ||
            prevValues.jump_block_mode !== currentValues.jump_block_mode
          }>
            {({ getFieldValue }) => {
              const jumpMode = String(getFieldValue('jump_mode') ?? '1');
              const jumpBlockMode = String(getFieldValue('jump_block_mode') ?? '3');

              return (
                <Row gutter={16}>
                  {/* 正常访问地址 */}
                  <Col span={12}>
                    {jumpMode === '1' && (
                      <Form.Item
                        name="default_url_input"
                        label="跳转URL地址"
                        rules={[
                          { required: true, message: '请输入跳转URL地址' },
                          {
                            validator: (_, value: string) => {
                              const list = parseDefaultURLInputValue(value);
                              if (list.length === 0) {
                                return Promise.reject(new Error('请至少填写一个跳转URL地址'));
                              }
                              const invalid = list.filter((url) => !SCHEME_URL_REGEX.test(url));
                              if (invalid.length > 0) {
                                const preview = invalid.slice(0, 3).join(', ');
                                return Promise.reject(new Error(`以下URL格式不正确：${preview}`));
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                        extra="支持输入多个URL地址（每行一个），系统会在正常访问时随机分流"
                      >
                        <Input.TextArea
                          placeholder={'例如：\nhttps://example.com\nhttps://example.org'}
                          autoSize={{ minRows: 3, maxRows: 6 }}
                        />
                      </Form.Item>
                    )}
                    {jumpMode === '2' && (
                      <Form.Item
                        name="default_url_input"
                        label="本地HTML文件路径"
                        rules={[
                          { required: true, message: '请输入HTML文件路径' },
                          {
                            pattern: /^[^:*?"<>|]+\.html$/i,
                            message: '请输入有效的HTML文件路径，如：money.html 或 a/index.html'
                          }
                        ]}
                        extra="填写HTML文件路径，如：money.html 或 a/index.html，支持相对路径（仅API调用支持）"
                      >
                        <Input placeholder="money.html 或 a/index.html" />
                      </Form.Item>
                    )}
                  </Col>

                  {/* 拦截访问地址 */}
                  <Col span={12}>
                    {jumpBlockMode === '1' && (
                      <Form.Item
                        name="intercept_url"
                        label="拦截URL地址"
                        rules={[
                          { required: true, message: '请输入拦截URL地址' },
                          {
                            validator: (_, value) => {
                              if (!value) {
                                return Promise.resolve();
                              }
                              return SCHEME_URL_REGEX.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('URL格式不正确，请填写合法的协议地址，例如 https://example.com 或 myapp://open'));
                            }
                          }
                        ]}
                        extra="支持任意协议的URL，例如 https://、whatsapp://、myapp:// 等"
                      >
                        <Input placeholder="例如：https://example.com 或 myapp://blocked" />
                      </Form.Item>
                    )}
                    {jumpBlockMode === '2' && deployTypeWatch === 'short_link' && (
                      <Form.Item
                        label="拦截HTML模板"
                        required
                        extra="可选择预置模板或直接填写自定义HTML文件名（文件需与短链模板目录保持一致）"
                      >
                        <Space direction="vertical" style={{ width: '100%' }} size={8}>
                          <Select
                            placeholder="选择模板"
                            allowClear
                            loading={deviceConstantsLoading && !deviceConstantsInitialized}
                            value={getFieldValue('intercept_url') || undefined}
                            options={templateSelectOptions}
                            onDropdownVisibleChange={(open) => {
                              if (open && !deviceConstantsInitialized) {
                                initializeDeviceConstants();
                              }
                            }}
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) => {
                              const label = String(option?.label ?? '');
                              const value = String(option?.value ?? '');
                              const keyword = input.toLowerCase();
                              return label.toLowerCase().includes(keyword) || value.toLowerCase().includes(keyword);
                            }}
                            onChange={(value) => {
                              form.setFieldsValue({ intercept_url: value ?? '' });
                            }}
                          />
                          <Form.Item
                            name="intercept_url"
                            noStyle
                            rules={[
                              { required: true, message: '请输入拦截HTML文件路径' },
                              {
                                pattern: /^[^:*?"<>|]+\.html$/i,
                                message: '请输入有效的HTML文件路径，如：safe.html 或 a/index.html',
                              },
                            ]}
                          >
                            <Input placeholder="例如：safe.html 或 a/index.html" />
                          </Form.Item>
                        </Space>
                      </Form.Item>
                    )}
                    {jumpBlockMode === '2' && deployTypeWatch !== 'short_link' && (
                      <Form.Item
                        name="intercept_url"
                        label="拦截HTML文件路径"
                        rules={[
                          { required: true, message: '请输入拦截HTML文件路径' },
                          {
                            pattern: /^[^:*?"<>|]+\.html$/i,
                            message: '请输入有效的HTML文件路径，如：safe.html 或 a/index.html',
                          },
                        ]}
                        extra="填写HTML文件路径，如：safe.html 或 a/index.html，支持相对路径（仅API调用支持）"
                      >
                        <Input placeholder="safe.html 或 a/index.html" />
                      </Form.Item>
                    )}
                    {jumpBlockMode === '3' && (
                      <Form.Item
                        name="intercept_url"
                        label="拦截处理"
                        tooltip="返回404错误时不需要填写任何内容"
                        extra="系统将直接返回404响应，无需填写内容"
                      >
                        <Input placeholder="无需填写" disabled />
                      </Form.Item>
                    )}
                  </Col>
                </Row>
              );
            }}
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            initialValue="audit"
          >
            <Select>
              {Object.entries(LINK_STATUS_CONFIG).map(([statusKey, config]) => (
                <Option key={statusKey} value={statusKey}>
                  {config.text}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* 访客控制配置 - 只保留重复访客控制，隐藏访问限制功能 */}
          <Card
            size="small"
            style={{
              marginBottom: 16,
              borderRadius: 'var(--border-radius-base)',
              border: '1px solid var(--color-border-light)',
              background: 'var(--color-bg-secondary)'
            }}
          >
            <Divider orientation="left" style={{ marginTop: 0 }}>
              <Space>
                <UserOutlined style={{ color: 'var(--color-primary)' }} />
                <span style={{ fontWeight: 600 }}>访客控制配置</span>
              </Space>
            </Divider>
            <Row gutter={24}>
              <Col span={12}>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Form.Item
                    name="duplicate_visitor_enabled"
                    label={
                      <span>
                        <Tag color="blue">重复访客控制</Tag>
                      </span>
                    }
                    initialValue={false}
                    valuePropName="checked"
                    style={{ marginBottom: 8 }}
                  >
                    <Switch
                      checkedChildren="开启"
                      unCheckedChildren="关闭"
                      size="default"
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                    开启后，同一访客在24小时内的重复访问将被记录和控制
                  </Text>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.duplicate_visitor_enabled !== currentValues.duplicate_visitor_enabled
                    }
                  >
                    {({ getFieldValue }) => {
                      const duplicateVisitorEnabled = getFieldValue('duplicate_visitor_enabled');
                      return duplicateVisitorEnabled ? (
                        <div style={{ marginTop: 12 }}>
                          <Form.Item
                            name="max_duplicate_count"
                            label={
                              <span>
                                <Text strong style={{ fontSize: '12px' }}>最大重复次数</Text>
                              </span>
                            }
                            initialValue={1}
                            style={{ marginBottom: 8 }}
                            rules={[
                              { required: true, message: '请输入最大重复次数' },
                              { type: 'number', min: 1, max: 10, message: '请输入1-10之间的数字' }
                            ]}
                          >
                            <InputNumber
                              size="small"
                              min={1}
                              max={100}
                              style={{ width: '100%' }}
                              placeholder="请输入最大重复次数"
                            />
                          </Form.Item>
                          <Text type="secondary" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                            24小时内允许的最大访问次数，超过将被拦截
                          </Text>
                        </div>
                      ) : null;
                    }}
                  </Form.Item>
                </Card>
              </Col>
              {/* 访问限制功能已隐藏 */}
            </Row>
          </Card>
        </Form>
      </Modal>

      {/* PHP 脚本下载弹窗 */}
      <Modal
        title={
          <Space>
            <DownloadOutlined />
            <span>下载 PHP 脚本</span>
            {phpModalRecord && (
              <Tag color="blue">{phpModalRecord.title || phpModalRecord.short_code}</Tag>
            )}
          </Space>
        }
        open={phpModalVisible}
        onCancel={() => setPhpModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setPhpModalVisible(false)}>取消</Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            loading={phpDownloading}
            onClick={handlePhpDownload}
          >
            下载 {phpDeployType === 'standalone' ? 'index.php' : 'fanghu_cloak.php'}
          </Button>,
        ]}
        width={640}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>选择部署模式：</Text>
        </div>
        <Radio.Group
          value={phpDeployType}
          onChange={(e) => setPhpDeployType(e.target.value)}
          style={{ width: '100%', marginBottom: 20 }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Radio value="standalone" style={{ width: '100%' }}>
              <Text strong>独立部署</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>适用于全新站点，上传 index.php 即可</Text>
            </Radio>
            <Radio value="wordpress" style={{ width: '100%' }}>
              <Text strong>WordPress 跳转模式</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>审核看 WP 内容，真实用户跳转到 offer</Text>
            </Radio>
            <Radio value="wordpress_dual" style={{ width: '100%' }}>
              <Text strong>WordPress 同域双库模式</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>同域名切换数据库，隐蔽性最高</Text>
            </Radio>
          </Space>
        </Radio.Group>

        <Divider style={{ margin: '12px 0' }} />

        {phpDeployType === 'standalone' && (
          <Alert
            type="info"
            showIcon
            message="独立部署步骤"
            description={
              <ol style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                <li>在宝塔面板创建网站，绑定域名</li>
                <li>将下载的 <Text code>index.php</Text> 上传到网站根目录</li>
                <li>
                  根据你在后台配置的跳转模式，按需上传 HTML 页面：
                  <ul style={{ marginTop: 4, paddingLeft: 16 }}>
                    <li>「正常访问模式」选了「显示本地HTML文件」：上传 B 面产品页面（如 <Text code>product/index.html</Text>）</li>
                    <li>「拦截访问模式」选了「显示本地HTML文件」：上传 A 面安全页（如 <Text code>safe/index.html</Text>）</li>
                    <li>其他模式（跳转到URL / 返回404错误）无需上传 HTML</li>
                  </ul>
                </li>
                <li>访问 <Text code>https://你的域名/?sd_debug=1</Text> 验证</li>
              </ol>
            }
          />
        )}

        {phpDeployType === 'wordpress' && (
          <Alert
            type="info"
            showIcon
            message="WordPress 跳转模式步骤"
            description={
              <ol style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                <li>将下载的 <Text code>fanghu_cloak.php</Text> 上传到 WordPress 根目录（和 <Text code>wp-config.php</Text> 同级）</li>
                <li>
                  编辑 WordPress 根目录下的 <Text code>index.php</Text>，在第一行 <Text code>{'<?php'}</Text> 后插入一行（如下对比）：
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>修改前：</div>
                    <pre style={{ background: '#f5f5f5', padding: '8px 12px', margin: '0 0 8px', borderRadius: 4, fontSize: 12 }}>
                      {`<?php
define( 'WP_USE_THEMES', true );
require __DIR__ . '/wp-blog-header.php';`}
                    </pre>
                    <div style={{ fontSize: 11, color: '#389e0d', marginBottom: 2 }}>修改后（加高亮行）：</div>
                    <pre style={{ background: '#f6ffed', padding: '8px 12px', margin: 0, borderRadius: 4, fontSize: 12, border: '1px solid #b7eb8f' }}>
                      {`<?php
require __DIR__ . '/fanghu_cloak.php';  ← 加这一行

define( 'WP_USE_THEMES', true );
require __DIR__ . '/wp-blog-header.php';`}
                    </pre>
                  </div>
                </li>
                <li>
                  在后台配置链接：
                  <ul style={{ marginTop: 4, paddingLeft: 16 }}>
                    <li>「正常访问模式」选「跳转到URL」</li>
                    <li>「跳转URL地址」填写 offer 落地页 URL（B 面，真实用户跳转到此）</li>
                    <li>「拦截访问模式」无需配置，A 面始终由 WordPress 渲染</li>
                  </ul>
                </li>
                <li>访问 <Text code>https://你的域名/?sd_debug=1</Text> 验证</li>
              </ol>
            }
          />
        )}

        {phpDeployType === 'wordpress_dual' && (
          <Alert
            type="warning"
            showIcon
            message="WordPress 同域双库模式步骤"
            description={
              <ol style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                <li>准备两个 WordPress 数据库（A 面安全内容 + B 面 offer 内容）</li>
                <li>将下载的 <Text code>fanghu_cloak.php</Text> 上传到 WordPress 根目录</li>

                <li>
                  编辑 <Text code>wp-config.php</Text>，找到数据库配置的三行，替换为以下内容：
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>修改前（原来的三行）：</div>
                    <pre style={{ background: '#f5f5f5', padding: '8px 12px', margin: '0 0 8px', borderRadius: 4, fontSize: 12 }}>
                      {`define( 'DB_NAME', 'wordpress_database' );
define( 'DB_USER', 'root' );
define( 'DB_PASSWORD', '123456' );`}
                    </pre>
                    <div style={{ fontSize: 11, color: '#389e0d', marginBottom: 2 }}>修改后（替换为，填入你的数据库信息）：</div>
                    <pre style={{ background: '#f6ffed', padding: '8px 12px', margin: 0, borderRadius: 4, fontSize: 12, border: '1px solid #b7eb8f' }}>
                      {`require __DIR__ . '/fanghu_cloak.php';
$fh_result = fanghuCloakCheck();
if ($fh_result === 'allow') {
    // B 面：真实用户看到（offer 产品页）
    define('DB_NAME', 'wordpress_b_database');  // ← 改成你的 B 面数据库名
    define('DB_USER', 'root');                  // ← 改成数据库用户名
    define('DB_PASSWORD', '123456');            // ← 改成数据库密码
} else {
    // A 面：审核/爬虫看到（正规安全内容）
    define('DB_NAME', 'wordpress_a_database');  // ← 改成你的 A 面数据库名
    define('DB_USER', 'root');                  // ← 同上
    define('DB_PASSWORD', '123456');            // ← 同上
}`}
                    </pre>
                  </div>
                </li>
                <li>访问 <Text code>https://你的域名/?sd_debug=1</Text> 验证</li>
              </ol>
            }
          />
        )}
      </Modal>

      {/* 统一的规则配置模态框 */}
      <RuleConfigModal
        visible={rulesModalVisible}
        mode="view"
        title={editingLink ? `规则配置 - ${editingLink.title}` : '规则配置'}
        rules={currentLinkRules}
        linkId={editingLink?.id}
        onCancel={() => setRulesModalVisible(false)}
        onSave={(rules) => {
          setCurrentLinkRules(rules);
          setRulesModalVisible(false);
          message.success('规则配置已保存');
        }}
      />
    </div>
  );
};

export default LinkManagement;
